<script setup lang="ts">
import ComboboxItem from '@ui/components/combobox/Item.vue'
import Icon from '@ui/components/icon/Index.vue'

import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot
} from '@headlessui/vue'

import { usePadding } from '@ui/composables/form/input/usePadding'
import { useVariant } from '@ui/composables/form/useVariant'

import type { Color, Size, Variant } from '@ui/types/form/input'

type Item = {
  value: string | number
  label: string
  search?: string | number // Optional field for additional search criteria
}
type ModelValue = Item | null

const emit = defineEmits<{
  (event: 'update:modelValue', value: ModelValue | undefined): void
}>()

const props = withDefaults(
  defineProps<{
    backgroundColor?: Color
    borderColor?: Color
    clearable?: boolean
    disabled?: boolean
    id?: string
    items: Item[]
    modelValue: ModelValue | undefined | null
    multiple?: boolean
    placeholder?: string
    size?: Size
    variant?: Variant
  }>(),
  {
    backgroundColor: 'white',
    borderColor: 'default',
    clearable: false,
    disabled: false,
    id: undefined,
    modelValue: undefined,
    items: undefined,
    multiple: false,
    placeholder: 'Select an item',
    size: 'md',
    variant: 'default'
  }
)

const { backgroundColor, borderColor, clearable, disabled, id, items, modelValue, placeholder, size, variant } =
  toRefs(props)

const query = ref('')

const inputValue = computed<Item | null>({
  get: () => modelValue.value ?? null,
  set: (value) => emit('update:modelValue', value)
})

const filteredItems = computed(() =>
  items.value.filter((item: Item) => {
    const match =
      item.label.toLowerCase().includes(query.value.toLowerCase()) ||
      item.value.toString().toLowerCase().includes(query.value.toLowerCase()) ||
      item.search?.toString().toLowerCase().includes(query.value.toLowerCase())

    return match
  })
)

const getLabelByInputValue = (item: Item) => {
  if (item) return item.label
  return ''
}

const clear = () => {
  inputValue.value = null
  query.value = ''
}
</script>

<template>
  <Combobox
    v-slot="{ open }"
    v-model="inputValue"
    :disabled="disabled"
    nullable
  >
    <div class="relative">
      <div class="relative w-full cursor-default rounded-lg bg-white">
        <ComboboxButton
          as="div"
          class="relative flex items-center border-none"
        >
          <ComboboxInput
            :id="id"
            :class="[
              'flex w-full rounded-md border focus:outline-none focus-visible:ring focus-visible:ring-blue-500',
              { 'opacity-75 pointer-events-none': disabled, 'pr-12': clearable && inputValue?.value },
              useTailwindClasses([
                useBackgroundColor(backgroundColor),
                useBorderColor(borderColor),
                usePadding(size),
                useTextSize(size),
                useVariant(variant)
              ])
            ]"
            v-bind="$attrs"
            :display-value="(item: any) => getLabelByInputValue(item)"
            autocomplete="off"
            spellcheck="false"
            :placeholder="placeholder"
            @change="query = $event.target.value"
          />

          <div
            :class="['append absolute rounded-r right-0 flex items-center text-gray-500 space-x-1 pr-3 bg-transparent']"
          >
            <button
              v-if="clearable && inputValue?.value"
              type="button"
              class="hover:text-black focus:text-black focus:outline-none focus-visible:ring focus-visible:ring-blue-500"
              @click.stop="clear"
            >
              <Icon :icon="XMarkIcon" />
            </button>
            <Icon
              :icon="open ? ChevronUpIcon : ChevronDownIcon"
              class="pointer-events-none"
            />
          </div>
        </ComboboxButton>
      </div>

      <TransitionRoot @after-leave="query = ''">
        <ComboboxOptions
          class="absolute z-30 mt-1 p-3 space-y-0.5 max-h-60 w-full overflow-auto rounded-lg bg-white border border-gray-300 shadow"
        >
          <div
            v-if="filteredItems.length === 0 && query !== ''"
            class="text-danger relative cursor-default select-none px-4 py-2"
          >
            <slot name="empty-message">No results match "{{ query }}"</slot>
          </div>
          <template v-else>
            <ComboboxOption
              v-for="(item, index) in filteredItems"
              v-slot="{ selected, active }"
              :key="index"
              :value="item"
              as="template"
            >
              <ComboboxItem
                :active="active"
                :selected="selected"
              >
                <slot
                  name="item"
                  :item="item"
                >
                  <!-- Fallback content if no slot is provided -->
                  {{ item.label }}
                </slot>
              </ComboboxItem>
            </ComboboxOption>
          </template>
          <div>
            <slot name="append" />
          </div>
        </ComboboxOptions>
      </TransitionRoot>
    </div>
  </Combobox>
</template>
